import { IGroupingExpression, ISortingStrategy, SortingDirection } from "@infragistics/igniteui-angular/core";
import { IgxGridComponent } from "@infragistics/igniteui-angular/grids/grid";
import { Utilities } from "@tylertech/shared-module";

/**
 * Orders groups by how many records they contain instead of by the group value.
 *
 * This is an `ISortingStrategy`, so it is assigned to the individual grouping expressions
 * (see `InfoGridComponent.applyGroupSortStrategy`) rather than to the grid itself. That way clicking a
 * group chip reorders the groups by count, while sorting a column from its header keeps using whatever
 * strategy that column's own sorting expression carries.
 */
export class GroupedGridSortingStrategy implements ISortingStrategy
{
    private static _instance: GroupedGridSortingStrategy = null;

    public static instance(): GroupedGridSortingStrategy
    {
        return this._instance ?? (this._instance = new GroupedGridSortingStrategy());
    }

    public sort(data: any[], fieldName: string, dir: SortingDirection, _ignoreCase: boolean,
        valueResolver: (obj: any, key: string, isDate?: boolean) => any, _isDate?: boolean, _isTime?: boolean, grid?: IgxGridComponent): any[]
    {
        const reverse = dir === SortingDirection.Desc ? -1 : 1;
        const expressions = this.groupKeyExpressions(fieldName, grid);

        const keys = new Map<any, string>();
        const counts = new Map<string, number>();
        for (const record of data)
        {
            const key = this.groupKey(record, expressions, valueResolver, grid);
            keys.set(record, key);
            counts.set(key, (counts.get(key) ?? 0) + 1);
        }

        return data.sort((first, second) =>
        {
            const firstKey = keys.get(first);
            const secondKey = keys.get(second);
            if (firstKey === secondKey)
                return 0;
            const difference = counts.get(firstKey) - counts.get(secondKey);
            if (difference !== 0)
                return reverse * difference;
            return reverse * (firstKey < secondKey ? -1 : 1);
        });
    }

    private groupKeyExpressions(fieldName: string, grid?: IgxGridComponent): IGroupingExpression[]
    {
        const expressions = grid?.groupingExpressions ?? [];
        const index = expressions.findIndex(e => e.fieldName === fieldName);
        return index < 0 ? [<IGroupingExpression>{ fieldName: fieldName }] : expressions.slice(0, index + 1);
    }

    private groupKey(record: any, expressions: IGroupingExpression[],
        valueResolver: (obj: any, key: string, isDate?: boolean) => any, grid?: IgxGridComponent): string
    {
        const values = expressions.map(expression =>
        {
            const value = valueResolver(record, expression.fieldName);
            if (Utilities.IsNullOrUndefined(value))
                return null;
            return expression.ignoreCase && this.isStringField(expression.fieldName, value, grid)
                ? `${value}`.toLowerCase()
                : `${value}`;
        });
        return JSON.stringify(values);
    }

    private isStringField(fieldName: string, value: any, grid?: IgxGridComponent): boolean
    {
        const dataType = grid?.getColumnByName?.(fieldName)?.dataType;
        return dataType ? dataType === "string" : typeof value === "string";
    }
}
